import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Phone, Mail, MessageSquare, Search, Trash2, CheckCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  converted: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-500',
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', source: '', q: '' });

  const token = () => localStorage.getItem('adminToken');

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.status) params.set('status', filter.status);
    if (filter.source) params.set('source', filter.source);
    if (filter.q) params.set('q', filter.q);
    try {
      const res = await fetch(`${API}/api/admin/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setLeads(data.leads || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
    } catch (e) {
      alert('Failed to load leads. Please re-login.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/leads/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status }),
      });
      fetchLeads();
    } catch (e) { alert('Update failed'); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    await fetch(`${API}/api/admin/leads/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token()}` },
    });
    fetchLeads();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="admin-leads-title">Leads Dashboard</h1>
            <p className="text-sm text-gray-500">All enquiries from enroll-now, enquire-now and CTA forms</p>
          </div>
          <Button onClick={fetchLeads} data-testid="refresh-leads-btn"><RefreshCw size={14} className="mr-2" /> Refresh</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <StatCard label="Total Leads" value={total} color="text-gray-900" />
          {['new', 'contacted', 'converted', 'lost'].map((s) => (
            <StatCard key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} value={stats[s] || 0} color={STATUS_STYLES[s]} testid={`stat-${s}`} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              placeholder="Search by name, phone, email…"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              className="pl-8 pr-3 py-2 border rounded-lg w-full text-sm"
              data-testid="leads-search"
            />
          </div>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="lost">Lost</option>
          </select>
          <select
            value={filter.source}
            onChange={(e) => setFilter({ ...filter, source: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
            data-testid="filter-source"
          >
            <option value="">All Sources</option>
            <option value="enquire-now">Enquire Now</option>
            <option value="enroll-now">Enroll Now</option>
            <option value="home-cta">Home CTA</option>
            <option value="landing-page">Landing Page</option>
            <option value="contact">Contact</option>
          </select>
        </div>

        {/* Leads Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-red-600" size={32} /></div>
            ) : leads.length === 0 ? (
              <div className="p-10 text-center text-gray-500" data-testid="no-leads-message">No leads yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-left">
                    <tr>
                      <th className="p-3">Name</th>
                      <th className="p-3">Phone</th>
                      <th className="p-3">Exam</th>
                      <th className="p-3">Source</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Received</th>
                      <th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-t hover:bg-gray-50" data-testid={`lead-row-${l.id}`}>
                        <td className="p-3">
                          <div className="font-medium">{l.name}</div>
                          {l.email && <div className="text-xs text-gray-500">{l.email}</div>}
                          {l.message && <div className="text-xs text-gray-400 truncate max-w-xs italic mt-1">"{l.message}"</div>}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-blue-600 hover:underline"><Phone size={12} /> {l.phone}</a>
                            <a href={`https://wa.me/91${l.phone}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-green-600 hover:underline text-xs"><MessageSquare size={11} /> WhatsApp</a>
                          </div>
                        </td>
                        <td className="p-3">{l.exam || '—'}</td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{l.source}</Badge></td>
                        <td className="p-3">
                          <select
                            value={l.status}
                            onChange={(e) => updateStatus(l.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded border-0 ${STATUS_STYLES[l.status]}`}
                            data-testid={`status-select-${l.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                            <option value="lost">Lost</option>
                          </select>
                        </td>
                        <td className="p-3 text-xs text-gray-500">{new Date(l.created_at).toLocaleDateString()}<br />{new Date(l.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</td>
                        <td className="p-3">
                          <Button size="sm" variant="ghost" onClick={() => deleteLead(l.id)} data-testid={`delete-lead-${l.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <Card data-testid={testid}><CardContent className="p-3 text-center">
    <p className="text-2xl font-bold text-red-600">{value}</p>
    <p className="text-xs text-gray-500 uppercase">{label}</p>
  </CardContent></Card>
);
